import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Lock, User, AlertCircle, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast, Toaster } from 'sonner';

// Always use tradepayflow.emergent.host for bonbot.biz custom domain
const getApiUrl = () => {
  const hostname = window.location.hostname;
  if (hostname === 'bonbot.biz' || hostname === 'www.bonbot.biz') {
    return 'https://bonbot.biz/api';
  }
  const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  return `${backendUrl}/api`;
};

const API = getApiUrl();

const AdminLogin = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('login'); // 'login' or 'setup'
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  const [adminExists, setAdminExists] = useState(false);

  // Check if admin exists on load
  useEffect(() => {
    const checkAdminExists = async () => {
      try {
        // Try to verify with empty token - if it returns specific error, admin exists
        const response = await fetch(`${API}/admin/verify?token=check`);
        const data = await response.json();
        // If we get a response (even invalid), admin system is set up
        setAdminExists(true);
        setMode('login');
      } catch (err) {
        // Even on error, default to login mode - admin likely exists
        // Only show setup if user explicitly requests it
        setAdminExists(true);
        setMode('login');
      } finally {
        setCheckingAdmin(false);
      }
    };

    // Check if already logged in
    const adminToken = localStorage.getItem('adminToken');
    if (adminToken) {
      verifyToken(adminToken);
    } else {
      checkAdminExists();
    }
  }, []);

  const verifyToken = async (token) => {
    try {
      const response = await fetch(`${API}/admin/verify?token=${token}`);
      const data = await response.json();
      if (data.valid) {
        navigate('/admin/dashboard');
      } else {
        localStorage.removeItem('adminToken');
        setCheckingAdmin(false);
      }
    } catch (err) {
      localStorage.removeItem('adminToken');
      setCheckingAdmin(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API}/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });

      const data = await response.json();

      if (data.success) {
        localStorage.setItem('adminToken', data.token);
        localStorage.setItem('adminUser', JSON.stringify(data.admin));
        toast.success('Login successful!');
        navigate('/admin/dashboard');
      } else {
        setError(data.message || 'Login failed');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API}/admin/setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password, email })
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Admin account created! Please login.');
        setMode('login');
        setPassword('');
        setAdminExists(true);
      } else {
        setError(data.message || 'Setup failed');
        if (data.message?.includes('already exists')) {
          setAdminExists(true);
          setMode('login');
        }
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (checkingAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4">
      <Toaster position="top-right" richColors />
      
      <Card className="w-full max-w-md bg-white/10 backdrop-blur-xl border-white/20">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold text-white">
            {mode === 'setup' ? 'Admin Setup' : 'Admin Login'}
          </CardTitle>
          <CardDescription className="text-gray-300">
            {mode === 'setup' 
              ? 'Create your admin account to manage guest users'
              : 'Sign in to manage guest users and approvals'
            }
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={mode === 'setup' ? handleSetup : handleLogin} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-200">Username</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="admin"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                />
              </div>
            </div>

            {mode === 'setup' && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-200">Email</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="email"
                    placeholder="admin@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-200">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                />
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-500/20 border border-red-500/30 text-red-300 text-sm rounded-lg">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading || !username || !password}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-6"
            >
              {loading ? 'Please wait...' : mode === 'setup' ? 'Create Admin Account' : 'Login'}
            </Button>

            {!adminExists && mode === 'login' && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => setMode('setup')}
                className="w-full text-gray-300 hover:text-white hover:bg-white/10"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                First time? Create admin account
              </Button>
            )}

            <div className="text-center pt-4">
              <a 
                href="/"
                className="text-sm text-gray-400 hover:text-white transition-colors"
              >
                ← Back to Dashboard
              </a>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLogin;
